'use strict';

/**
 * PictView-ProjectTypes: manage a tenant's Project types (V54 / F163). A Project type is the reusable shape a
 * Project of a Kind takes -- its metadata form, held as an external, versioned MetadataManifest. Types are not
 * hand-authored here: a person describes one (and uploads reference files) and a node designs the form across a
 * conversation, previewing it, until the person consents and the node commits it (POST /1.0/ProjectType/Commit,
 * behind the RunStep consent gate). This screen is the catalog and the launch point for that loop.
 *
 * Two routes: /project-types (the catalog) and /project-type/:id (one type, its form, and an Adjust launcher);
 * the body repaints into #ProjectTypes-Body so a change does not re-render the shell.
 *
 * Create and Adjust both open a lightweight authoring Project (Kind 'project-type-authoring'): the description
 * and the uploaded files become its corpus, so they ride the Project dispatch envelope the node already reads
 * (the corpus-inlining path), rather than per-turn attachments the node cannot fetch. The authoring Project is
 * the workspace; the committed artifact is a ProjectType, which shows back up in this catalog.
 *
 * @author Steven Velozo <steven@velozo.com>
 * @license MIT
 */

const libPictView = require('pict-view');
const libFrameworkClient = require('../providers/FrameworkClient.js');

// The reserved Kind of the throwaway Project that hosts a type-authoring conversation, and the corpus role
// that carries the "adjust this existing type" target for the node to read.
const _AUTHORING_KIND = 'project-type-authoring';
const _ADJUST_ROLE = 'AdjustTarget';

class PictViewProjectTypes extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._types = [];
		this._type = null;
		this._manifest = null;
		this._manifestSource = null;
		this._busy = false;
	}

	_typeAPI() { return this.pict.providers.ProjectTypeAPI; }
	_projectAPI() { return this.pict.providers.ProjectAPI; }
	_sourceAPI() { return this.pict.providers.ProjectSourceAPI; }
	_app() { return this.pict.PictApplication; }

	_esc(pValue)
	{
		return String(pValue === null || typeof pValue === 'undefined' ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

	_body() { return (typeof document !== 'undefined') ? document.getElementById('ProjectTypes-Body') : null; }
	_paint(pHTML) { let tmpBody = this._body(); if (tmpBody) { tmpBody.innerHTML = pHTML; } }
	_ensureShell() { if (!this._body()) { this.render(); } }

	_toast(pMessage, pType)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && tmpModal.toast) { tmpModal.toast(pMessage, { type: pType || 'success' }); }
	}

	_pill(pStatus)
	{
		let tmpStatus = String(pStatus || 'Draft');
		let tmpSlug = tmpStatus.toLowerCase().replace(/[^a-z0-9]/g, '');
		return '<span class="pt-pill pt-pill-' + tmpSlug + '">' + this._esc(tmpStatus) + '</span>';
	}

	show() { return this.showList(); }

	// ---- the catalog (route /project-types) ----------------------------------------------------------------
	showList()
	{
		this._ensureShell();
		this._enterList();
		return true;
	}

	async _enterList()
	{
		this._type = null;
		this._paint('<div class="pt-loading">Loading project types...</div>');
		let tmpTypes = [];
		try { tmpTypes = await this._typeAPI().resolve({ PageSize: 200 }); }
		catch (pError) { /* a transient failure paints an empty catalog rather than a blank pane */ }
		this._types = (Array.isArray(tmpTypes) ? tmpTypes : [])
			.sort((pA, pB) => String(pA.Name || '').localeCompare(String(pB.Name || '')));
		this._paintList();
	}

	_paintList()
	{
		let tmpCards = this._types.map((pType) => ''
			+ '<button class="pt-card" onclick="_Pict.views[\'Plansheet-ProjectTypes\'].openType(' + (Number(pType.IDProjectType) || 0) + ')">'
			+ '<div class="pt-card-head"><span class="pt-card-name">' + this._esc(pType.Name || pType.TypeKey || 'Untitled type') + '</span>' + this._pill(pType.Status) + '</div>'
			+ (pType.TypeKey ? ('<div class="pt-card-key">' + this._esc(pType.TypeKey) + '</div>') : '')
			+ (pType.Description ? ('<div class="pt-card-sum">' + this._esc(pType.Description) + '</div>') : '')
			+ '</button>').join('');
		this._paint(''
			+ '<div class="pt-new">'
			+ '<div class="pt-group-head">Create a project type</div>'
			+ '<p class="pt-new-hint">Describe the type you want and, in the workspace that opens, attach any example files. The assistant designs the form, shows you a preview, and takes your feedback until you approve it.</p>'
			+ '<div class="pt-new-row">'
			+ '<input type="text" id="ProjectType-NewName" class="pt-input" placeholder="What is this type? (e.g. Vendor Invoice)" />'
			+ '<button class="pt-btn" onclick="_Pict.views[\'Plansheet-ProjectTypes\'].createType()">Start</button>'
			+ '</div></div>'
			+ (tmpCards ? ('<div class="pt-cards">' + tmpCards + '</div>') : '<div class="pt-empty">No project types yet. Describe one above to design its form with the assistant.</div>'));
	}

	async createType()
	{
		if (this._busy) { return; }
		let tmpNameEl = (typeof document !== 'undefined') ? document.getElementById('ProjectType-NewName') : null;
		let tmpName = tmpNameEl ? String(tmpNameEl.value || '').trim() : '';
		if (!tmpName) { this._toast('Say what the type is first.', 'error'); return; }
		this._busy = true;
		let tmpProject = null;
		try
		{
			tmpProject = await this._projectAPI().create(
				{ Name: tmpName, Kind: _AUTHORING_KIND, Status: 'Draft', Summary: 'Designing the "' + tmpName + '" project type.' });
		}
		catch (pError) { this._busy = false; this._toast((pError && pError.message) || 'Could not start the type.', 'error'); return; }
		this._busy = false;
		let tmpID = Number(tmpProject && tmpProject.IDProject) || 0;
		// The authoring conversation lives in the Project workspace (corpus upload + the Create Project Type
		// task). Hand off to it; the committed type returns to this catalog.
		if (tmpID && this._app() && typeof this._app().showProject === 'function') { this._app().showProject(tmpID); }
		else { this._enterList(); }
	}

	// ---- one type (route /project-type/:id) ----------------------------------------------------------------
	openType(pID) { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showProjectType === 'function') { tmpApp.showProjectType(Number(pID) || 0); } else { this.showDetail(pID); } }

	async showDetail(pID)
	{
		this._ensureShell();
		let tmpID = Number(pID) || 0;
		this._paint('<div class="pt-loading">Loading...</div>');
		let tmpType = null;
		try { tmpType = await this._typeAPI().read(tmpID); }
		catch (pError) { tmpType = null; }
		if (!tmpType) { return this.showList(); }
		this._type = tmpType;
		this._manifest = null;
		this._manifestSource = null;
		let tmpKey = String(tmpType.TypeKey || '');
		if (tmpKey)
		{
			try
			{
				let tmpResolved = await libFrameworkClient.getJSON(this.pict, '/1.0/MetadataManifest/Resolve/Project/' + encodeURIComponent(tmpKey));
				if (tmpResolved && tmpResolved.Manifest) { this._manifest = tmpResolved.Manifest; this._manifestSource = tmpResolved.Source || null; }
			}
			catch (pError) { /* the form summary is best-effort; the type still renders without it */ }
		}
		this._paintDetail();
	}

	_paintDetail()
	{
		let tmpType = this._type || {};
		let tmpManifest = this._manifest || {};
		let tmpDescriptors = (tmpManifest && typeof tmpManifest.Descriptors === 'object') ? tmpManifest.Descriptors : {};
		let tmpSections = Array.isArray(tmpManifest.Sections) ? tmpManifest.Sections : [];
		let tmpFieldKeys = Object.keys(tmpDescriptors);

		let tmpFieldRows = tmpFieldKeys.map((pKey) =>
		{
			let tmpDescriptor = tmpDescriptors[pKey] || {};
			let tmpForm = tmpDescriptor.PictForm || {};
			return '<div class="pt-field"><span class="pt-field-name">' + this._esc(tmpDescriptor.Name || pKey) + '</span>'
				+ '<span class="pt-field-meta">' + this._esc(tmpForm.InputType || 'Text') + (tmpForm.Section ? (' &middot; ' + this._esc(tmpForm.Section)) : '') + '</span></div>';
		}).join('');

		let tmpVersionNote = this._manifestSource
			? ('Form version ' + (Number(this._manifestSource.Version) || 0) + (this._manifestSource.Default ? ' (built-in default)' : ''))
			: 'No form resolved yet';

		this._paint(''
			+ '<button class="pt-back" onclick="_Pict.views[\'Plansheet-ProjectTypes\'].backToList()">&larr; All project types</button>'
			+ '<div class="pt-head"><h2 class="pt-title">' + this._esc(tmpType.Name || tmpType.TypeKey || 'Project type') + '</h2>'
			+ (tmpType.TypeKey ? ('<span class="pt-key-chip">' + this._esc(tmpType.TypeKey) + '</span>') : '') + this._pill(tmpType.Status) + '</div>'
			+ (tmpType.Description ? ('<p class="pt-sum">' + this._esc(tmpType.Description) + '</p>') : '')
			+ '<div class="pt-panel">'
			+ '<div class="pt-group-head">The form</div>'
			+ '<div class="pt-form-meta">' + this._esc(tmpVersionNote) + ' &middot; ' + tmpFieldKeys.length + ' field' + (tmpFieldKeys.length === 1 ? '' : 's') + ' in ' + tmpSections.length + ' section' + (tmpSections.length === 1 ? '' : 's') + '</div>'
			+ (tmpFieldRows ? ('<div class="pt-fields">' + tmpFieldRows + '</div>') : '<div class="pt-empty-sm">This type has no fields yet.</div>')
			+ '</div>'
			+ '<div class="pt-panel">'
			+ '<div class="pt-group-head">Adjust this type</div>'
			+ '<p class="pt-new-hint">Describe a change (add a field, rename a section, change a widget). The assistant revises the form, previews it, and publishes a new version once you approve -- existing projects keep the version they were saved against.</p>'
			+ '<button class="pt-btn" onclick="_Pict.views[\'Plansheet-ProjectTypes\'].adjustType()">Adjust with the assistant</button>'
			+ '</div>');
	}

	backToList() { let tmpApp = this._app(); if (tmpApp && typeof tmpApp.showProjectTypes === 'function') { tmpApp.showProjectTypes(); } else { this.showList(); } }

	async adjustType()
	{
		if (this._busy || !this._type) { return; }
		this._busy = true;
		let tmpType = this._type;
		let tmpProject = null;
		try
		{
			tmpProject = await this._projectAPI().create(
				{ Name: 'Adjust: ' + (tmpType.Name || tmpType.TypeKey || 'type'), Kind: _AUTHORING_KIND, Status: 'Draft', Summary: 'Adjusting the "' + (tmpType.Name || tmpType.TypeKey) + '" project type.' });
		}
		catch (pError) { this._busy = false; this._toast((pError && pError.message) || 'Could not start the adjustment.', 'error'); return; }

		// Carry the target type and its current form into the authoring corpus as inline Data, so the node's
		// adjust recipe knows which type it is revising and starts from the shape it already has.
		let tmpIDProject = Number(tmpProject && tmpProject.IDProject) || 0;
		if (tmpIDProject)
		{
			let tmpTarget = { IDProjectType: Number(tmpType.IDProjectType) || 0, TypeKey: tmpType.TypeKey || '', Name: tmpType.Name || '', CurrentManifest: this._manifest || null };
			try
			{
				await this._sourceAPI().create(
					{ IDProject: tmpIDProject, Role: _ADJUST_ROLE, Name: 'Adjustment target', ContentJSON: JSON.stringify(tmpTarget), SortOrder: 0 });
			}
			catch (pError) { /* best-effort: the workspace still opens; the node can be told the target in chat */ }
		}
		this._busy = false;
		if (tmpIDProject && this._app() && typeof this._app().showProject === 'function') { this._app().showProject(tmpIDProject); }
	}
}

const PictViewProjectTypesConfiguration =
{
	ViewIdentifier: 'Plansheet-ProjectTypes',
	DefaultRenderable: 'Plansheet-ProjectTypes',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.pt-wrap { max-width: 900px; margin: 0 auto; padding: 24px 16px 64px; }
		.pt-head-top h1 { margin: 0 0 4px; font-size: 1.6em; color: var(--theme-color-text-primary, #1d2230); }
		.pt-sub { margin: 0 0 20px; color: var(--theme-color-text-secondary, #5b6376); }
		.pt-loading, .pt-empty { color: var(--theme-color-text-secondary, #5b6376); padding: 24px 0; }
		.pt-empty-sm { color: var(--theme-color-text-secondary, #5b6376); font-size: 0.9em; padding: 6px 0; }
		.pt-group-head { font-size: 0.8em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 10px; }
		.pt-new { margin-bottom: 22px; }
		.pt-new-hint { font-size: 0.9em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.5; margin: 0 0 10px; }
		.pt-new-row { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
		.pt-input { padding: 9px 12px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 8px; background: var(--theme-color-background-secondary, #f3f5f9); color: var(--theme-color-text-primary, #1d2230); font: inherit; box-sizing: border-box; flex: 1 1 280px; }
		.pt-btn { padding: 9px 18px; border: 1px solid var(--theme-color-brand-primary, #3b6fd4); border-radius: 8px; background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; cursor: pointer; font-size: 0.95em; }
		.pt-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 12px; }
		.pt-card { text-align: left; cursor: pointer; padding: 14px 16px; border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 10px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1d2230); }
		.pt-card:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.pt-card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
		.pt-card-name { font-weight: 600; }
		.pt-card-key { font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); margin-top: 4px; font-family: ui-monospace, Menlo, Consolas, monospace; }
		.pt-card-sum { font-size: 0.88em; color: var(--theme-color-text-secondary, #5b6376); line-height: 1.4; margin-top: 4px; }
		.pt-back { background: none; border: none; color: var(--theme-color-brand-primary, #3b6fd4); cursor: pointer; padding: 0; font-size: 0.9em; margin-bottom: 12px; }
		.pt-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
		.pt-title { margin: 0; font-size: 1.35em; color: var(--theme-color-text-primary, #1d2230); }
		.pt-key-chip { font-size: 0.72em; letter-spacing: 0.02em; padding: 2px 9px; border-radius: 999px; background: var(--theme-color-background-secondary, #f3f5f9); border: 1px solid var(--theme-color-border-default, #d8dde6); color: var(--theme-color-text-secondary, #5b6376); font-family: ui-monospace, Menlo, Consolas, monospace; }
		.pt-sum { color: var(--theme-color-text-secondary, #5b6376); margin: 8px 0 0; line-height: 1.5; }
		.pt-panel { margin-top: 24px; border-top: 1px solid var(--theme-color-border-default, #d8dde6); padding-top: 16px; }
		.pt-form-meta { font-size: 0.85em; color: var(--theme-color-text-secondary, #5b6376); margin-bottom: 10px; }
		.pt-fields { display: flex; flex-direction: column; gap: 2px; }
		.pt-field { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; padding: 7px 10px; border-radius: 8px; }
		.pt-field:hover { background: var(--theme-color-background-hover, #f3f5f9); }
		.pt-field-name { color: var(--theme-color-text-primary, #1d2230); }
		.pt-field-meta { font-size: 0.78em; color: var(--theme-color-text-secondary, #5b6376); }
		.pt-pill { display: inline-block; font-size: 0.62em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 9px; border-radius: 11px; background: #9e9e9e; color: #fff; }
		.pt-pill-draft { background: #9e9e9e; }
		.pt-pill-active { background: #2E7D32; }
		.pt-pill-retired { background: #c0353a; }
	`,
	Templates:
	[
		{
			Hash: 'Plansheet-ProjectTypes',
			Template: /*html*/`
<div class="pt-wrap">
	<div class="pt-head-top">
		<h1>Project Types</h1>
		<p class="pt-sub">Reusable project shapes your team can start from. Describe one and the assistant designs its form with you.</p>
	</div>
	<div id="ProjectTypes-Body"></div>
</div>`
		}
	],
	Renderables:
	[
		{ RenderableHash: 'Plansheet-ProjectTypes', TemplateHash: 'Plansheet-ProjectTypes', ContentDestinationAddress: '#Plansheet-Workspace', RenderMethod: 'replace' }
	]
};

module.exports = PictViewProjectTypes;
module.exports.default_configuration = PictViewProjectTypesConfiguration;
